import { useCallback, useState } from "react";

import {
  ExportAssetsResponse,
  GetTransferLocationsResponse,
  TransferLocation,
} from "@/components/iconik/types";

import { useApi } from "../useApi";

/**
 * Data access for the Clip Preview "Transfer" action.
 *
 * - ``getLocations`` loads the approved transfer destinations the user may
 *   pick (GET /transfer-locations — Iconik storages).
 * - ``transferAssets`` submits a transfer of one or more assets to a chosen
 *   storage (POST /transfer). The backend authorizes the destination and
 *   queues the work in Iconik, so a resolved promise means "submitted".
 */
const useTransferLocations = () => {
  const [locations, setLocations] = useState<TransferLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { apiGet, apiPost } = useApi();

  const getLocations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGet<GetTransferLocationsResponse>(
        "/transfer-locations"
      );
      setLocations(response?.locations ?? []);
    } catch (e) {
      console.error(e);
      setError("Failed to load transfer locations");
    } finally {
      setLoading(false);
    }
  }, [apiGet]);

  const transferAssets = useCallback(
    async (locationId: string, assetIds: string[]): Promise<void> => {
      const response = await apiPost<ExportAssetsResponse>("/transfer", {
        locationId,
        assetIds,
      });
      if (!response || response.status !== "submitted") {
        throw new Error("transfer request failed");
      }
    },
    [apiPost]
  );

  return { locations, loading, error, getLocations, transferAssets };
};

export default useTransferLocations;
